import * as Sentry from '@sentry/react';

/**
 * Initialise Sentry for the React frontend.
 * Call once at cold-start in main.tsx, before ReactDOM.render.
 *
 * Reads VITE_SENTRY_DSN from environment — when absent Sentry silently no-ops.
 * @sentry/vite-plugin in vite.config.ts handles source-map uploads during CI builds.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `planacle-web@${import.meta.env.VITE_APP_VERSION ?? '0.0.0'}`,

    // Sample 10 % of transactions in production to keep quota manageable
    tracesSampleRate: 0.1,

    // Attach stack traces to all captured messages, not just exceptions
    attachStacktrace: true,

    // Scrub common PII fields before sending
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
      }
      return event;
    },
  });
}

/**
 * Attach (or clear) the currently authenticated user on every Sentry event.
 * Call from AuthContext whenever auth state changes.
 *
 * @param uid Firebase UID, or null to clear the user context on sign-out.
 */
export function setSentryUser(uid: string | null): void {
  if (uid) {
    Sentry.setUser({ id: uid });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Report a caught error from an async code path (service call, hook, effect).
 * Normalises any unknown catch value to an Error before passing to Sentry.
 * Safe to call whether or not Sentry is initialised — SDK silently no-ops when DSN is absent.
 *
 * @param error   The caught value — typically Error | unknown from a catch block
 * @param context Optional extra key/value pairs attached to the Sentry event for debugging
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(err, context ? { extra: context } : undefined);
}
